import { Button } from "@/components/ui/button";
import { Menu, X, ChevronDown } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerTrigger,
} from "@/components/ui/drawer";

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [homeDropdownOpen, setHomeDropdownOpen] = useState(false);
  const dropdownTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const location = useLocation();

  // Close drawer on route change to prevent scroll lock issues
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  const menuItems = [
    { name: "실험동물", path: "/animal-list" },
    { name: "자료실", path: "/resources" },
    { name: "문의", path: "/contact" },
    { name: "주문", path: "/order" },
  ];

  const homeDropdownItems = [
    { name: "회사소개", path: "/about" },
    { name: "공지사항", path: "/notice" },
  ];

  const isActive = (path: string) => location.pathname === path;

  const handleDropdownEnter = () => {
    if (dropdownTimeout.current) {
      clearTimeout(dropdownTimeout.current);
    }
    setHomeDropdownOpen(true);
  };

  const handleDropdownLeave = () => {
    dropdownTimeout.current = setTimeout(() => {
      setHomeDropdownOpen(false);
    }, 300); // 300ms 지연
  };

  const handleMobileNavigation = (path: string) => {
    // Ensure proper cleanup before navigation
    setIsOpen(false);
    // Force body scroll unlock in case drawer doesn't clean up properly
    setTimeout(() => {
      document.body.style.overflow = '';
    }, 100);
  };

  return (
    <header className="bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border sticky top-0 z-50 w-full">
      <div className="container max-w-7xl mx-auto container-padding">
        <div className="flex h-16 items-center justify-between">
          <Link to="/" className="flex items-center flex-shrink-0">
            <div className="text-center">
              <div className="font-bold text-lg lg:text-xl text-foreground flex items-center justify-center">
                Central <span className="border-2 border-jax-blue text-jax-blue w-7 h-7 lg:w-8 lg:h-8 mx-1 flex items-center justify-center rounded-full text-sm lg:text-base font-bold shadow-md">TG</span> Inc.
              </div>
              <div className="text-[9px] lg:text-[10px] text-muted-foreground">Transgenic Animal Models</div>
            </div>
          </Link>
          
          <nav className="hidden lg:flex items-center justify-end flex-1">
            <div className="flex items-center space-x-12">
              {/* 홈 dropdown */}
              <div className="relative group">
                <button 
                  className={`flex items-center space-x-1 font-medium transition-colors group-hover:text-jax-blue ${
                    isActive("/") || isActive("/about") || isActive("/notice")
                      ? "text-jax-blue"
                      : "text-foreground"
                  }`}
                  onMouseEnter={handleDropdownEnter}
                  onMouseLeave={handleDropdownLeave}
                >
                  <span>홈</span>
                  <ChevronDown className="w-4 h-4 transition-transform group-hover:rotate-180" />
                </button>
                
                {homeDropdownOpen && (
                  <div 
                    className="absolute top-full left-0 mt-1 w-48 bg-background border border-border rounded-lg shadow-lg z-50"
                    onMouseEnter={handleDropdownEnter}
                    onMouseLeave={handleDropdownLeave}
                  >
                    <div className="py-2">
                      {homeDropdownItems.map((item) => (
                        <Link
                          key={item.path}
                          to={item.path}
                          className={`block px-4 py-3 transition-colors font-medium ${
                            isActive(item.path)
                              ? "bg-jax-blue text-white"
                              : "text-foreground hover:bg-muted hover:text-jax-blue"
                          }`}
                        >
                          {item.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Regular menu items */}
              {menuItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`transition-colors font-medium whitespace-nowrap hover:text-jax-blue ${
                    isActive(item.path)
                      ? "text-jax-blue"
                      : "text-foreground"
                  }`}
                >
                  {item.name}
                </Link>
              ))}
            </div>
          </nav>

          <div className="flex items-center">
            {/* Mobile Menu */}
            <Drawer open={isOpen} onOpenChange={setIsOpen}>
              <DrawerTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden">
                  <Menu className="w-5 h-5" />
                </Button>
              </DrawerTrigger>
              <DrawerContent>
                <div className="flex flex-col p-6">
                  <div className="flex items-center justify-between mb-6">
                    <span className="text-lg font-semibold">메뉴</span>
                    <DrawerClose asChild>
                      <Button variant="ghost" size="icon">
                        <X className="w-5 h-5" />
                      </Button>
                    </DrawerClose>
                  </div>
                  
                  <div className="space-y-2">
                    {/* Home section with dropdown items */}
                    <div className="space-y-1">
                      <div className="px-4 py-2 text-sm font-medium text-muted-foreground">홈</div>
                      {homeDropdownItems.map((item) => (
                        <Link
                          key={item.path}
                          to={item.path}
                          onClick={() => handleMobileNavigation(item.path)}
                          className={`block py-3 px-6 rounded-lg transition-colors font-medium ${
                            isActive(item.path)
                              ? "bg-jax-blue text-white"
                              : "text-foreground hover:bg-muted"
                          }`}
                        >
                          {item.name}
                        </Link>
                      ))}
                    </div>
                    
                    {/* Regular menu items */}
                    <div className="pt-2 space-y-1">
                      {menuItems.map((item) => (
                        <Link
                          key={item.path}
                          to={item.path}
                          onClick={() => handleMobileNavigation(item.path)}
                          className={`block py-3 px-4 rounded-lg transition-colors font-medium ${
                            isActive(item.path)
                              ? "bg-jax-blue text-white"
                              : "text-foreground hover:bg-muted"
                          }`}
                        >
                          {item.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </DrawerContent>
            </Drawer>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
